import { useEffect, useState, CSSProperties } from 'react';
import { ModalBoxProps } from '../ModalBox';
import styled from 'styled-components';
import { FormSize, TextInput } from '@ardoq/forms';
import { isEqual } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { newFilterTerm } from './customSelectActions';
import { ClearIndicatorStyle } from './atoms';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { useTextInputState } from './useTextInputState';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const useTextInputStyle = (props: {
  getHostBox: ModalBoxProps['getHostBox'];
}) => {
  const [style, setStyle] = useState<CSSProperties | undefined>();
  useEffect(() => {
    const { left, top, width, height } = props.getHostBox();
    const newStyle = {
      top,
      left,
      width,
      height,
    };
    if (!isEqual(style, newStyle)) {
      setStyle(newStyle);
    }
  }, [props, style]);

  return style;
};

const TextInputWrapper = styled.div`
  position: fixed;
  left: -10000px;
`;

type TextInputContainerProps = {
  getHostBox: ModalBoxProps['getHostBox'];
  clickNamespace?: string;
  placeholderText: string;
  onFilterChanged?: (filterTerm: string) => void;
  defaultValue?: string;
  onClear?: () => void;
  closeModal: () => void;
  hasCustomSelectButton: boolean;
  additionalSearchControls?: React.ReactNode;
  i18nLocale?: I18nLocale;
};

const ClearContainerStyle = styled.div`
  position: absolute;
  right: 28px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SelectTextInput = ({
  getHostBox,
  placeholderText,
  onFilterChanged = (filterTerm: string) =>
    dispatchAction(newFilterTerm(filterTerm)),
  defaultValue,
  onClear,
  closeModal,
  hasCustomSelectButton,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: TextInputContainerProps) => {
  const style = useTextInputStyle({ getHostBox });
  const { handleValueChange, inputRef, onClearClick, hasClearButton } =
    useTextInputState(onFilterChanged, onClear, defaultValue);

  return (
    <TextInputWrapper style={style}>
      <TextInputContent
        placeholderText={placeholderText}
        handleValueChange={handleValueChange}
        hasCustomSelectButton={hasCustomSelectButton}
        defaultValue={defaultValue}
        hasClearButton={hasClearButton}
        inputRef={inputRef}
        closeModal={closeModal}
        onClearClick={onClearClick}
        showCloseChevron
        i18nLocale={i18nLocale}
      />
    </TextInputWrapper>
  );
};

export const SelectTextInputInDropdown = ({
  placeholderText,
  onFilterChanged = (filterTerm: string) =>
    dispatchAction(newFilterTerm(filterTerm)),
  defaultValue,
  onClear,
  closeModal,
  hasCustomSelectButton,
  additionalSearchControls,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: TextInputContainerProps) => {
  const { handleValueChange, inputRef, onClearClick, hasClearButton } =
    useTextInputState(onFilterChanged, onClear, defaultValue);

  return (
    <FlexBox paddingBottom="small" width="full" flexDirection="row" gap="small">
      <TextInputContent
        placeholderText={placeholderText}
        handleValueChange={handleValueChange}
        hasCustomSelectButton={hasCustomSelectButton}
        defaultValue={defaultValue}
        hasClearButton={hasClearButton}
        inputRef={inputRef}
        closeModal={closeModal}
        onClearClick={onClearClick}
        showCloseChevron={false}
        i18nLocale={i18nLocale}
      />
      {additionalSearchControls}
    </FlexBox>
  );
};

type TextInputContentProps = {
  placeholderText: string;
  handleValueChange: (value: string) => void;
  hasCustomSelectButton: boolean;
  defaultValue?: string;
  hasClearButton: boolean;
  inputRef: React.Ref<HTMLInputElement>;
  closeModal: () => void;
  onClearClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  showCloseChevron: boolean;
  i18nLocale: I18nLocale;
};

const TextInputContent = ({
  placeholderText,
  handleValueChange,
  hasCustomSelectButton,
  defaultValue,
  hasClearButton,
  inputRef,
  closeModal,
  onClearClick,
  showCloseChevron,
  i18nLocale,
}: TextInputContentProps) => {
  const hasCloseChevron = hasCustomSelectButton && showCloseChevron;
  return (
    <>
      <TextInput
        placeholder={placeholderText}
        autoFocus
        onValueChange={handleValueChange}
        hasTransparentBackground={!hasCustomSelectButton}
        hasRightPadding
        defaultValue={defaultValue}
        hasClearButton={hasClearButton}
        ref={inputRef}
        rightIconName={hasCloseChevron ? IconName.CHEVRON_UP : undefined}
        rightIconOnClick={hasCloseChevron ? closeModal : undefined}
        rightIconTooltip={t(
          i18nLocale,
          'dropdownMenu:closeOptionList',
          'Close option list'
        )}
        formSize={FormSize.FULL}
      />
      {hasClearButton && (
        <ClearContainerStyle onClick={onClearClick}>
          <ClearIndicatorStyle
            role="button"
            data-test-id={'clear-all'}
            aria-label={t(
              i18nLocale,
              'dropdownMenu:clearValueAriaLabel',
              'Clear value'
            )}
          >
            {t(i18nLocale, 'dropdownMenu:clear', 'Clear')}
          </ClearIndicatorStyle>
        </ClearContainerStyle>
      )}
    </>
  );
};
